import * as angular from 'angular';
import './styling.scss';
import * as template from './template.html';

import * as _ from 'lodash';
import { getCreditCardExpirationMoment } from 'common/source/utilities/policyInfoHelper';
import { HippoApiService } from 'services/hippoApi';
import { CurrentUserService } from 'services/currentUser';
import { IRootScopeService } from 'angular';
import { PermissionService } from 'services/permission';
import * as hippoConstFactory from 'common/hippoConst.js';

const module = angular.module('credit-card-payment', []);

interface CreditCardPaymentScope extends angular.IScope {
    creditCardForm: any;
}

interface CreditCardPaymentController extends angular.IStrictController {
    policyInfo: CustomerPolicyInfo;
    edit: () => void;
    lastFourDigits: string;
    expirationDate: string;
    cardType: string;
    isEditMode: boolean;
    cancel: () => void;
    save: () => void;
    toggle: () => void;
    hasPastFailedPayment: boolean;
    stripeCallback: (result: stripe.TokenResponse | undefined) => void;
    updateHasChangedCardInfo: (update: boolean) => void;
    paymentMethodUpdateStatus: string;
    showPaymentMessage: boolean;
    paymentStatusText: string;
    paymentStatusSubText: string;
    showSpinner: boolean;
    showEditPaymentButton: boolean;
    active: boolean;
    hasChangedCardInfo: boolean;
    cardGotSuccessUpdated: boolean;
    onPaymentMethodChange: () => void;
}

const STRIPE_CARD_ELEMENT_SELECTOR = '#card-element';
const STRIPE_CARD_ERRORS_ELEMENT_SELECTOR = '#card-error';
const PAYMENT_STATUSES_TO_RECHARGE_UPON_CREDIT_CARD_UPDATE: Array<PaymentStatus> = [
    'due',
    'failed',
    'not_received',
    'outstanding'
];

const CARD_INFO: Array<string> = ['cardNumber', 'cardExpiry', 'cardCvc'];

module.component('creditCardPaymentMethod', {
    bindings: {
        policyInfo: '<',
        title: '<',
        onPaymentMethodChange: '&'
    },
    template: template,
    // TODO: Alvin to convert the following controller to class style
    // disabling this line because it's the last legacy example of a too-big function, so the new standard can be set
    // tslint:disable-next-line:no-big-function
    controller: function(
        this: CreditCardPaymentController,
        $timeout,
        $scope: CreditCardPaymentScope,
        $stateParams: StateParams,
        $element: angular.IAugmentedJQuery,
        $q: angular.IQService,
        hippoApiService: HippoApiService,
        currentUserService: CurrentUserService,
        $rootScope: IRootScopeService,
        permissionService: PermissionService
    ) {
        this.$onInit = () => {
            const creditCardInfo = this.policyInfo.checkout_data.cc_info;
            this.hasPastFailedPayment = _.includes(
                PAYMENT_STATUSES_TO_RECHARGE_UPON_CREDIT_CARD_UPDATE,
                this.policyInfo.payment_status
            );

            if (!creditCardInfo) {
                this.edit();
            } else {
                updatePaymentInformationDisplay();
                this.isEditMode = false;
            }

            this.edit();
        };

        const setErrorMessage = (errorMessage: string) => {
            const cardError = document.querySelector(STRIPE_CARD_ERRORS_ELEMENT_SELECTOR);

            if (!cardError) {
                return;
            }

            cardError.textContent = errorMessage;
        };

        const saveStripeToken = (stripeToken: stripe.Token) => {
            const policyNumber = this.policyInfo.id;

            const policyInfoUpdate: CustomerPolicyInfoUpdate = {
                checkout_data: {
                    payment_method: 'cc',
                    payment_token: stripeToken.id
                }
            };

            return hippoApiService.updateCustomerPolicy(policyNumber, policyInfoUpdate);
        };

        const updatePaymentInformationDisplay = () => {
            this.lastFourDigits = this.policyInfo.checkout_data.cc_info.last4;
            this.expirationDate = getCreditCardExpirationMoment(this.policyInfo).format('MM/YY');
            this.cardType = _.kebabCase(this.policyInfo.checkout_data.cc_info.brand);
            this.showEditPaymentButton = permissionService.canEditPaymentMethod(this.policyInfo);
        };
        const exitEditMode = () => {
            this.showSpinner = false;
            this.isEditMode = false;
            $scope.$emit('closePaymentEditSection');
        };

        const clearPaymentStatus = () => {
            this.paymentStatusSubText = '';
            this.paymentStatusText = '';
            this.cardGotSuccessUpdated = false;
            this.hasChangedCardInfo = false;
        };

        const showPaymentFailureMessage = () => {
            const HIPPO_CONSTS = hippoConstFactory();
            setErrorMessage('');
            this.paymentMethodUpdateStatus = 'failure';
            this.paymentStatusText = 'Payment unsuccessful.';
            this.paymentStatusSubText = `Please review your payment information and give us a call at
                             <a href="tel:1-${HIPPO_CONSTS.HIPPO_SUPPORT_PHONE_NUMBER}">${
                HIPPO_CONSTS.HIPPO_SUPPORT_PHONE_NUMBER
            }</a> if you continue experience difficulties.`;
            this.showSpinner = false;
        };

        const updateCustomerPolicyInfo = () => {
            currentUserService.getUpdatedCustomerPolicyInfos().then((result) => {
                $rootScope.$emit('updatedCustomerPolicyInfos');
                $rootScope.$emit('updatedPaymentFrequency');
                exitEditMode();
            });
        };

        this.updateHasChangedCardInfo = (update: boolean) => {
            this.hasChangedCardInfo = update;
        };

        this.$onChanges = (changesObj) => {
            const policyInfo = changesObj.policyInfo;
            if (policyInfo && policyInfo.currentValue !== undefined) {
                $timeout(() => {
                    this.policyInfo = policyInfo.currentValue;
                    updatePaymentInformationDisplay();
                });
            }
        };

        this.edit = () => {
            this.isEditMode = true;
            this.showPaymentMessage = true;
            this.paymentMethodUpdateStatus = '';
            clearPaymentStatus();
        };

        this.cancel = () => {
            clearPaymentStatus();
            exitEditMode();
        };

        this.stripeCallback = (result) => {
            if (result && result.error && result.error.message) {
                setErrorMessage(result.error.message);
            } else if (result && result.token) {
                this.showSpinner = true;

                saveStripeToken(result.token)
                    .then(() => {
                        updateCustomerPolicyInfo();
                        clearPaymentStatus();
                        this.cardGotSuccessUpdated = true;
                        this.onPaymentMethodChange();
                        $scope.$emit('closePaymentEditSection');
                    })
                    .catch(() => {
                        showPaymentFailureMessage();
                    });
            } else {
                showPaymentFailureMessage();
            }
        };

        this.save = () => {
            this.showPaymentMessage = true;

            if (!this.hasChangedCardInfo) {
                return;
            }
            $scope.$broadcast('stripeCardSave');
        };
    }
});

export { module };
